import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { useTranslation } from 'react-i18next';

// ─── Deadline Form ────────────────────────────────────────────────────────────

const EMPTY_DEADLINE = { label: '', startDate: '', endDate: '', minGames: '', maxGames: '' };

// ─── Icon options for Season Pass levels ──────────────────────────────────────

const LEVEL_ICONS = [
  { value: null, label: '—', emoji: '' },
  { value: 'fire', label: 'Fire', emoji: '🔥' },
  { value: 'star', label: 'Star', emoji: '⭐' },
  { value: 'trophy', label: 'Trophy', emoji: '🏆' },
  { value: 'medal', label: 'Medal', emoji: '🏅' },
  { value: 'crown', label: 'Crown', emoji: '👑' },
  { value: 'lightning', label: 'Lightning', emoji: '⚡' },
  { value: 'shield', label: 'Shield', emoji: '🛡️' },
  { value: 'sword', label: 'Sword', emoji: '⚔️' },
  { value: 'rocket', label: 'Rocket', emoji: '🚀' },
  { value: 'basketball', label: 'Basketball', emoji: '🏀' },
  { value: 'target', label: 'Target', emoji: '🎯' },
  { value: 'diamond', label: 'Diamond', emoji: '💎' },
  { value: 'lock', label: 'Lock', emoji: '🔒' },
  { value: 'muscle', label: 'Muscle', emoji: '💪' },
  { value: 'brain', label: 'Brain', emoji: '🧠' },
  { value: 'gear', label: 'Gear', emoji: '⚙️' },
  { value: 'sparkles', label: 'Sparkles', emoji: '✨' },
  { value: 'skull', label: 'Skull', emoji: '💀' },
  { value: 'badge', label: 'Badge', emoji: '🎖️' },
  { value: 'refresh', label: 'Refresh', emoji: '🔄' },
];

const getIconEmoji = (iconValue) => {
  const icon = LEVEL_ICONS.find(i => i.value === iconValue);
  return icon ? icon.emoji : '';
};

// ─── Level Form ───────────────────────────────────────────────────────────────

const EMPTY_LEVEL = { gamesRequired: '', rewardDescription: '', icon: null, rewardT1: '', rewardT2: '', rewardT3: '', rewardT4: '' };

// ─── Rookie & Soph Level Form ─────────────────────────────────────────────────

const EMPTY_RS_LEVEL = { gamesRequired: '', upgradeDescription: '', limitationDescription: '', icon: null };

const RookieSophForm = ({ initial, nextLevel, maxGames, onSave, onCancel, t }) => {
  const [data, setData] = useState(initial || { ...EMPTY_RS_LEVEL });
  const [error, setError] = useState('');

  const set = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (data.gamesRequired === '' || data.gamesRequired === null || data.gamesRequired === undefined)
      return 'Games required is required.';
    const games = parseInt(data.gamesRequired, 10);
    if (isNaN(games) || games < 0) return 'Games required must be a valid non-negative number.';
    if (maxGames !== null && maxGames !== undefined && games > maxGames)
      return `Games required (${games}) cannot exceed season maximum (${maxGames}).`;
    if (!data.upgradeDescription || !data.upgradeDescription.trim())
      return 'Upgrade description is required.';
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    onSave({
      level: nextLevel,
      gamesRequired: parseInt(data.gamesRequired, 10),
      upgradeDescription: data.upgradeDescription.trim(),
      limitationDescription: data.limitationDescription ? data.limitationDescription.trim() : null,
      icon: data.icon || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.levelNumber')}
          </label>
          <input
            type="number"
            value={nextLevel}
            disabled
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.gamesRequired')} *
          </label>
          <input
            type="number"
            min="0"
            max={maxGames || undefined}
            value={data.gamesRequired}
            onChange={e => set('gamesRequired', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder="0"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {maxGames !== null && maxGames !== undefined
              ? t('admin.seasonManagement.maxGamesNote', { max: maxGames })
              : t('admin.seasonManagement.noMaxGamesNote')}
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.upgradeDescription')} *
          </label>
          <input
            type="text"
            value={data.upgradeDescription}
            onChange={e => set('upgradeDescription', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder={t('admin.seasonManagement.upgradeDescriptionPlaceholder')}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.limitationDescription')}
          </label>
          <textarea
            value={data.limitationDescription || ''}
            onChange={e => set('limitationDescription', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder={t('admin.seasonManagement.limitationDescriptionPlaceholder')}
            rows={2}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.icon')}
          </label>
          <div className="flex flex-wrap gap-2">
            {LEVEL_ICONS.map((ic) => (
              <button
                key={ic.value || 'none'}
                type="button"
                onClick={() => set('icon', ic.value)}
                className={`px-3 py-2 rounded-lg border text-sm transition ${
                  data.icon === ic.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 bg-white dark:bg-gray-800'
                }`}
                title={ic.label}
              >
                {ic.emoji || t('admin.seasonManagement.noIcon')}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
        >
          {t('admin.seasonManagement.saveLevel')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

const LevelForm = ({ initial, nextLevel, maxGames, onSave, onCancel, t }) => {
  const [data, setData] = useState(initial || { ...EMPTY_LEVEL });
  const [error, setError] = useState('');

  const set = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (data.gamesRequired === '' || data.gamesRequired === null || data.gamesRequired === undefined)
      return 'Games required is required.';
    const games = parseInt(data.gamesRequired, 10);
    if (isNaN(games) || games < 0) return 'Games required must be a valid non-negative number.';
    if (maxGames !== null && maxGames !== undefined && games > maxGames)
      return `Games required (${games}) cannot exceed season maximum (${maxGames}).`;
    if (!data.rewardDescription || !data.rewardDescription.trim())
      return 'Reward description is required.';
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    onSave({
      level: nextLevel,
      gamesRequired: parseInt(data.gamesRequired, 10),
      rewardDescription: data.rewardDescription.trim(),
      rewardT1: (data.rewardT1 || '').trim(),
      rewardT2: (data.rewardT2 || '').trim(),
      rewardT3: (data.rewardT3 || '').trim(),
      rewardT4: (data.rewardT4 || '').trim(),
      icon: data.icon || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.levelNumber')}
          </label>
          <input
            type="number"
            value={nextLevel}
            disabled
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.gamesRequired')} *
          </label>
          <input
            type="number"
            min="0"
            max={maxGames || undefined}
            value={data.gamesRequired}
            onChange={e => set('gamesRequired', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder="0"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {maxGames !== null && maxGames !== undefined
              ? t('admin.seasonManagement.maxGamesNote', { max: maxGames })
              : t('admin.seasonManagement.noMaxGamesNote')}
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.rewardDescription')} *
          </label>
          <input
            type="text"
            value={data.rewardDescription}
            onChange={e => set('rewardDescription', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder={t('admin.seasonManagement.rewardDescriptionPlaceholder')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.seasonManagement.rewardDescriptionHint')}
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('admin.seasonManagement.tierRewards')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('admin.seasonManagement.tierRewardsHint')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['T1', 'T2', 'T3', 'T4'].map((tier) => (
              <div key={tier}>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{tier}</label>
                <textarea
                  rows={4}
                  value={data[`reward${tier}`] || ''}
                  onChange={e => set(`reward${tier}`, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.icon')}
          </label>
          <div className="flex flex-wrap gap-2">
            {LEVEL_ICONS.map((ic) => (
              <button
                key={ic.value || 'none'}
                type="button"
                onClick={() => set('icon', ic.value)}
                className={`px-3 py-2 rounded-lg border text-sm transition ${
                  data.icon === ic.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 bg-white dark:bg-gray-800'
                }`}
                title={ic.label}
              >
                {ic.emoji || t('admin.seasonManagement.noIcon')}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
        >
          {t('admin.seasonManagement.saveLevel')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

const DeadlineForm = ({ initial, onSave, onCancel, t }) => {
  const [data, setData] = useState(initial || EMPTY_DEADLINE);
  const [error, setError] = useState('');

  const set = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (!data.label.trim()) return t('admin.seasonManagement.validation.labelRequired');
    if (!data.startDate) return t('admin.seasonManagement.validation.startDateRequired');
    if (!data.endDate) return t('admin.seasonManagement.validation.endDateRequired');
    if (new Date(data.startDate) > new Date(data.endDate))
      return t('admin.seasonManagement.validation.startBeforeEnd');
    if (data.minGames === '' || data.minGames === null || data.minGames === undefined)
      return t('admin.seasonManagement.validation.minGamesRequired');
    const min = parseInt(data.minGames, 10);
    if (isNaN(min) || min < 0) return t('admin.seasonManagement.validation.minGamesInvalid');
    if (data.maxGames !== '' && data.maxGames !== null && data.maxGames !== undefined) {
      const max = parseInt(data.maxGames, 10);
      if (isNaN(max) || max < 0) return t('admin.seasonManagement.validation.maxGamesInvalid');
      if (max < min) return t('admin.seasonManagement.validation.maxGamesGteMin');
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    onSave({
      ...data,
      minGames: parseInt(data.minGames, 10),
      maxGames: (data.maxGames !== '' && data.maxGames !== null && data.maxGames !== undefined)
        ? parseInt(data.maxGames, 10)
        : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.deadlineLabel')} *
          </label>
          <input
            type="text"
            value={data.label}
            onChange={e => set('label', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder={t('admin.seasonManagement.deadlineLabelPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.deadlineStartDate')} *
          </label>
          <input
            type="date"
            value={data.startDate}
            onChange={e => set('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.deadlineEndDate')} *
          </label>
          <input
            type="date"
            value={data.endDate}
            onChange={e => set('endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.deadlineMinGames')} *
          </label>
          <input
            type="number"
            min="0"
            value={data.minGames}
            onChange={e => set('minGames', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.seasonManagement.deadlineMaxGames')}
          </label>
          <input
            type="number"
            min="0"
            value={data.maxGames}
            onChange={e => set('maxGames', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            placeholder={t('admin.seasonManagement.noLimit')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.seasonManagement.deadlineMaxGamesHelp')}</p>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
        >
          {t('admin.seasonManagement.saveDeadline')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SeasonManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  // ── Setup assistant state (AP6) ────────────────────────────────────────────
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [setupMessage, setSetupMessage] = useState('');
  const [materializing, setMaterializing] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);

  // ── General form state ─────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    playoffFormat: 'conference',
    seriesFormat: 'Best-of-7',
    description: '',
    isActive: false,
    cantCutRuleActive: true,
    seasonType: 'standard',
    rosterEra: '',
    hasFantasyDraft: false,
    fantasyDraftName: '',
    draftRounds: 13,
    draftOrder: 'standard'
  });

  // ── Custom playoff config state ────────────────────────────────────────────
  const [customPlayoffConfig, setCustomPlayoffConfig] = useState({
    participants: 16,
    conferenceDependent: false,
    rounds: [
      { name: 'Round 1', seriesFormat: 'Best-of-1' },
      { name: 'Round 2', seriesFormat: 'Best-of-1' },
      { name: 'Semi-Finals', seriesFormat: 'Best-of-5' },
      { name: 'Finals', seriesFormat: 'Best-of-5' }
    ]
  });

  const getDefaultRounds = (participants) => {
    const numRounds = Math.log2(participants);
    const rounds = [];
    let teamsInRound = participants;
    for (let i = 0; i < numRounds; i++) {
      let name;
      if (i === numRounds - 1) name = 'Finals';
      else if (i === numRounds - 2) name = 'Semi-Finals';
      else name = `Round ${i + 1}`;
      rounds.push({ name, seriesFormat: 'Best-of-7' });
      teamsInRound = teamsInRound / 2;
    }
    return rounds;
  };

  // ── Gameplay settings state ────────────────────────────────────────────────
  const [gameplaySettings, setGameplaySettings] = useState({ maxGames: '', minGamesForPlayoffs: '', maxTradesPerTeam: '', maxTradedPlayersPerTeam: '' });
  const [gameplaySettingsError, setGameplaySettingsError] = useState('');
  const [gameplaySettingsSaving, setGameplaySettingsSaving] = useState(false);

  // ── Deadlines state ────────────────────────────────────────────────────────
  const [deadlines, setDeadlines] = useState([]);
  const [deadlinesLoading, setDeadlinesLoading] = useState(false);
  const [showDeadlineForm, setShowDeadlineForm] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);

  // ── Season Pass state ──────────────────────────────────────────────────────
  const [seasonPassLevels, setSeasonPassLevels] = useState([]);
  const [seasonPassLoading, setSeasonPassLoading] = useState(false);
  const [showLevelForm, setShowLevelForm] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);

  // ── Rookies & Sophs state ─────────────────────────────────────────────────
  const [rookieSophLevels, setRookieSophLevels] = useState([]);
  const [rookieSophLoading, setRookieSophLoading] = useState(false);
  const [showRSForm, setShowRSForm] = useState(false);
  const [editingRSLevel, setEditingRSLevel] = useState(null);

  // ── Attribute Upgrades state ──────────────────────────────────────────────
  const [attributeUpgradeSettings, setAttributeUpgradeSettings] = useState({ attributeUpgradeGames: '', attributeUpgradePoints: '' });
  const [attributeUpgradeError, setAttributeUpgradeError] = useState('');
  const [attributeUpgradeSaving, setAttributeUpgradeSaving] = useState(false);

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await api.get('/seasons?includeFantasyDraft=true');
      setSeasons(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching seasons:', error);
      setLoading(false);
    }
  };

  const fetchDeadlines = useCallback(async (seasonId) => {
    setDeadlinesLoading(true);
    try {
      const response = await api.get(`/seasons/${seasonId}/deadlines`);
      setDeadlines(response.data);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    } finally {
      setDeadlinesLoading(false);
    }
  }, []);

  // ── Setup assistant (AP6) ──────────────────────────────────────────────────
  const fetchSetupStatus = useCallback(async (seasonId) => {
    setSetupLoading(true);
    setSetupError('');
    try {
      const response = await api.get(`/seasons/${seasonId}/setup-status`);
      setSetupStatus(response.data);
    } catch (error) {
      console.error('Error fetching setup status:', error);
      setSetupError(error.response?.data?.error || t('admin.seasonManagement.setupLoadError'));
      setSetupStatus(null);
    } finally {
      setSetupLoading(false);
    }
  }, [t]);

  const handleMaterializeRoster = async () => {
    if (!editingSeason) return;
    setMaterializing(true);
    setSetupError('');
    setSetupMessage('');
    try {
      const response = await api.post(`/seasons/${editingSeason.id}/materialize-roster`);
      const { created, skipped, total } = response.data;
      setSetupMessage(t('admin.seasonManagement.setupMaterializeDone', { created, skipped, total }));
      await fetchSetupStatus(editingSeason.id);
    } catch (error) {
      console.error('Error materializing roster:', error);
      setSetupError(error.response?.data?.error || t('admin.seasonManagement.setupMaterializeError'));
    } finally {
      setMaterializing(false);
    }
  };

  const handleSnapshotGMs = async () => {
    if (!editingSeason) return;
    setSnapshotting(true);
    setSetupError('');
    setSetupMessage('');
    try {
      const response = await api.post(`/seasons/${editingSeason.id}/snapshot-gms`);
      const { created, total, alreadyFrozen, existing } = response.data;
      setSetupMessage(alreadyFrozen
        ? t('admin.seasonManagement.setupSnapshotAlready', { existing })
        : t('admin.seasonManagement.setupSnapshotDone', { created, total }));
      await fetchSetupStatus(editingSeason.id);
    } catch (error) {
      console.error('Error snapshotting GMs:', error);
      setSetupError(error.response?.data?.error || t('admin.seasonManagement.setupSnapshotError'));
    } finally {
      setSnapshotting(false);
    }
  };

  const fetchSeasonPass = useCallback(async (seasonId) => {
    setSeasonPassLoading(true);
    try {
      const response = await api.get(`/seasons/${seasonId}/season-pass`);
      setSeasonPassLevels(response.data);
    } catch (error) {
      console.error('Error fetching season pass:', error);
    } finally {
      setSeasonPassLoading(false);
    }
  }, []);

  const fetchRookieSoph = useCallback(async (seasonId) => {
    setRookieSophLoading(true);
    try {
      const response = await api.get(`/seasons/${seasonId}/rookie-soph`);
      setRookieSophLevels(response.data);
    } catch (error) {
      console.error('Error fetching rookies & sophs:', error);
    } finally {
      setRookieSophLoading(false);
    }
  }, []);

  const fetchAttributeUpgrades = useCallback(async (seasonId) => {
    try {
      const response = await api.get(`/seasons/${seasonId}/attribute-upgrades`);
      setAttributeUpgradeSettings({
        attributeUpgradeGames: response.data.attributeUpgradeGames ?? '',
        attributeUpgradePoints: response.data.attributeUpgradePoints ?? ''
      });
    } catch (error) {
      console.error('Error fetching attribute upgrades:', error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (formData.playoffFormat === 'custom') {
        submitData.playoffConfig = customPlayoffConfig;
      }
      if (editingSeason) {
        await api.put(`/seasons/${editingSeason.id}`, submitData);
      } else {
        await api.post('/seasons', submitData);
      }
      fetchSeasons();
      resetForm();
    } catch (error) {
      console.error('Error saving season:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error saving season';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleGameplaySettingsSave = async () => {
    setGameplaySettingsError('');
    const maxGamesVal = gameplaySettings.maxGames;
    const minGamesVal = gameplaySettings.minGamesForPlayoffs;

    if (maxGamesVal !== '' && maxGamesVal !== null) {
      const max = parseInt(maxGamesVal, 10);
      if (isNaN(max) || max < 0) {
        setGameplaySettingsError(t('admin.seasonManagement.validation.maxGamesInvalid'));
        return;
      }
    }
    if (minGamesVal !== '' && minGamesVal !== null) {
      const min = parseInt(minGamesVal, 10);
      if (isNaN(min) || min < 0) {
        setGameplaySettingsError(t('admin.seasonManagement.validation.minGamesInvalid'));
        return;
      }
    }
    if (maxGamesVal !== '' && minGamesVal !== '') {
      const max = parseInt(maxGamesVal, 10);
      const min = parseInt(minGamesVal, 10);
      if (!isNaN(max) && !isNaN(min) && max < min) {
        setGameplaySettingsError(t('admin.seasonManagement.validation.maxLtMinPlayoffs'));
        return;
      }
    }

    for (const field of ['maxTradesPerTeam', 'maxTradedPlayersPerTeam']) {
      const value = gameplaySettings[field];
      if (value !== '' && value !== null) {
        const parsed = parseInt(value, 10);
        if (isNaN(parsed) || parsed < 0) {
          setGameplaySettingsError(t('admin.seasonManagement.validation.tradeLimitInvalid'));
          return;
        }
      }
    }

    setGameplaySettingsSaving(true);
    try {
      await api.put(`/seasons/${editingSeason.id}/gameplay`, {
        maxGames: gameplaySettings.maxGames === '' ? null : gameplaySettings.maxGames,
        minGamesForPlayoffs: gameplaySettings.minGamesForPlayoffs === '' ? null : gameplaySettings.minGamesForPlayoffs,
        maxTradesPerTeam: gameplaySettings.maxTradesPerTeam === '' ? null : gameplaySettings.maxTradesPerTeam,
        maxTradedPlayersPerTeam: gameplaySettings.maxTradedPlayersPerTeam === '' ? null : gameplaySettings.maxTradedPlayersPerTeam
      });
      fetchSeasons();
      alert(t('admin.seasonManagement.gameplaySettingsSaved'));
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      setGameplaySettingsError(msg);
    } finally {
      setGameplaySettingsSaving(false);
    }
  };

  const handleActivate = async (seasonId) => {
    if (window.confirm(t('admin.seasonManagement.seasonActivated'))) {
      try {
        await api.put(`/seasons/${seasonId}/activate`);
        fetchSeasons();
      } catch (error) {
        alert(error.response?.data?.error || t('common.error'));
      }
    }
  };

  const handleDelete = async (seasonId) => {
    if (!window.confirm('Are you sure you want to delete this season? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/seasons/${seasonId}`);
      fetchSeasons();
      alert('Season deleted successfully!');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.gameCount) {
        const gameCount = error.response.data.gameCount;
        const confirmForce = window.confirm(
          `This season has ${gameCount} game(s).\n\n` +
          `⚠️ WARNING: Force deleting will permanently delete:\n` +
          `• The season\n` +
          `• All ${gameCount} game(s)\n` +
          `• All associated game statistics\n\n` +
          `This action CANNOT be undone!\n\n` +
          `Type "DELETE" in the next prompt to confirm.`
        );
        if (confirmForce) {
          const confirmation = window.prompt(
            `⚠️ FINAL WARNING ⚠️\n\n` +
            `You are about to permanently delete:\n` +
            `• Season ID: ${seasonId}\n` +
            `• ${gameCount} game(s) and all statistics\n\n` +
            `Type DELETE (in capital letters) to confirm:`
          );
          if (confirmation === 'DELETE') {
            try {
              await api.delete(`/seasons/${seasonId}?force=true`);
              fetchSeasons();
              alert(`Season and ${gameCount} game(s) deleted successfully!`);
            } catch (forceError) {
              alert(forceError.response?.data?.error || 'Error force deleting season');
            }
          } else {
            alert('Deletion cancelled - confirmation text did not match.');
          }
        }
      } else {
        alert(error.response?.data?.error || 'Error deleting season');
      }
    }
  };

  const handleEdit = (season) => {
    setEditingSeason(season);
    setFormData({
      name: season.name,
      year: season.year,
      startDate: season.startDate,
      endDate: season.endDate || '',
      playoffFormat: season.playoffFormat || 'conference',
      seriesFormat: season.seriesFormat || 'Best-of-7',
      description: season.description || '',
      isActive: season.isActive,
      cantCutRuleActive: season.cantCutRuleActive !== undefined ? season.cantCutRuleActive : true,
      seasonType: season.seasonType || 'standard',
      rosterEra: season.rosterEra || '',
      hasFantasyDraft: season.hasFantasyDraft || false,
      fantasyDraftName: season.fantasyDraft?.name || '',
      draftRounds: season.fantasyDraft?.totalRounds || 13,
      draftOrder: season.fantasyDraft?.draftOrder || 'standard'
    });
    // Load custom playoff config if exists
    if (season.playoffFormat === 'custom' && season.playoffConfig) {
      try {
        const config = typeof season.playoffConfig === 'string' ? JSON.parse(season.playoffConfig) : season.playoffConfig;
        setCustomPlayoffConfig(config);
      } catch (e) {
        console.error('Error parsing playoffConfig:', e);
      }
    }
    setGameplaySettings({
      maxGames: season.maxGames !== null && season.maxGames !== undefined ? season.maxGames : '',
      minGamesForPlayoffs: season.minGamesForPlayoffs !== null && season.minGamesForPlayoffs !== undefined
        ? season.minGamesForPlayoffs
        : '',
      maxTradesPerTeam: season.maxTradesPerTeam !== null && season.maxTradesPerTeam !== undefined
        ? season.maxTradesPerTeam
        : '',
      maxTradedPlayersPerTeam: season.maxTradedPlayersPerTeam !== null && season.maxTradedPlayersPerTeam !== undefined
        ? season.maxTradedPlayersPerTeam
        : ''
    });
    setGameplaySettingsError('');
    setActiveTab('general');
    setShowDeadlineForm(false);
    setEditingDeadline(null);
    setShowLevelForm(false);
    setEditingLevel(null);
    setShowRSForm(false);
    setEditingRSLevel(null);
    fetchDeadlines(season.id);
    fetchSeasonPass(season.id);
    fetchRookieSoph(season.id);
    fetchAttributeUpgrades(season.id);
    setSetupStatus(null);
    setSetupError('');
    setSetupMessage('');
    fetchSetupStatus(season.id);
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      year: new Date().getFullYear(),
      startDate: '',
      endDate: '',
      playoffFormat: 'conference',
      seriesFormat: 'Best-of-7',
      description: '',
      isActive: false,
      cantCutRuleActive: true,
      seasonType: 'standard',
      rosterEra: '',
      hasFantasyDraft: false,
      fantasyDraftName: '',
      draftRounds: 13,
      draftOrder: 'standard'
    });
    setGameplaySettings({ maxGames: '', minGamesForPlayoffs: '', maxTradesPerTeam: '', maxTradedPlayersPerTeam: '' });
    setGameplaySettingsError('');
    setCustomPlayoffConfig({
      participants: 16,
      conferenceDependent: false,
      rounds: [
        { name: 'Round 1', seriesFormat: 'Best-of-1' },
        { name: 'Round 2', seriesFormat: 'Best-of-1' },
        { name: 'Semi-Finals', seriesFormat: 'Best-of-5' },
        { name: 'Finals', seriesFormat: 'Best-of-5' }
      ]
    });
    setDeadlines([]);
    setShowDeadlineForm(false);
    setEditingDeadline(null);
    setSeasonPassLevels([]);
    setShowLevelForm(false);
    setEditingLevel(null);
    setRookieSophLevels([]);
    setShowRSForm(false);
    setEditingRSLevel(null);
    setAttributeUpgradeSettings({ attributeUpgradeGames: '', attributeUpgradePoints: '' });
    setAttributeUpgradeError('');
    setEditingSeason(null);
    setActiveTab('general');
    setShowCreateForm(false);
  };

  // ── Deadline CRUD handlers ─────────────────────────────────────────────────

  const handleDeadlineSave = async (data) => {
    try {
      if (editingDeadline) {
        await api.put(`/seasons/${editingSeason.id}/deadlines/${editingDeadline.id}`, data);
      } else {
        await api.post(`/seasons/${editingSeason.id}/deadlines`, data);
      }
      setShowDeadlineForm(false);
      setEditingDeadline(null);
      fetchDeadlines(editingSeason.id);
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert(`Error: ${msg}`);
    }
  };

  const handleDeadlineEdit = (deadline) => {
    setEditingDeadline(deadline);
    setShowDeadlineForm(true);
  };

  const handleDeadlineDelete = async (deadlineId) => {
    if (!window.confirm(t('admin.seasonManagement.confirmDeleteDeadline'))) return;
    try {
      await api.delete(`/seasons/${editingSeason.id}/deadlines/${deadlineId}`);
      fetchDeadlines(editingSeason.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Error deleting deadline');
    }
  };

  const handleDeadlineCancel = () => {
    setShowDeadlineForm(false);
    setEditingDeadline(null);
  };

  // ── Season Pass CRUD handlers ──────────────────────────────────────────────

  const handleLevelSave = async (data) => {
    try {
      if (editingLevel) {
        await api.put(`/seasons/${editingSeason.id}/season-pass/${editingLevel.id}`, data);
      } else {
        await api.post(`/seasons/${editingSeason.id}/season-pass`, data);
      }
      setShowLevelForm(false);
      setEditingLevel(null);
      fetchSeasonPass(editingSeason.id);
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert(`Error: ${msg}`);
    }
  };

  const handleLevelEdit = (level) => {
    setEditingLevel(level);
    setShowLevelForm(true);
  };

  const handleLevelDelete = async (levelId) => {
    if (!window.confirm(t('admin.seasonManagement.confirmDeleteLevel'))) return;
    try {
      await api.delete(`/seasons/${editingSeason.id}/season-pass/${levelId}`);
      fetchSeasonPass(editingSeason.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Error deleting level');
    }
  };

  const handleLevelCancel = () => {
    setShowLevelForm(false);
    setEditingLevel(null);
  };

  // ── Rookies & Sophs CRUD handlers ─────────────────────────────────────────

  const handleRSSave = async (data) => {
    try {
      if (editingRSLevel) {
        await api.put(`/seasons/${editingSeason.id}/rookie-soph/${editingRSLevel.id}`, data);
      } else {
        await api.post(`/seasons/${editingSeason.id}/rookie-soph`, data);
      }
      setShowRSForm(false);
      setEditingRSLevel(null);
      fetchRookieSoph(editingSeason.id);
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert(`Error: ${msg}`);
    }
  };

  const handleRSEdit = (level) => {
    setEditingRSLevel(level);
    setShowRSForm(true);
  };

  const handleRSDelete = async (levelId) => {
    if (!window.confirm(t('admin.seasonManagement.confirmDeleteLevel'))) return;
    try {
      await api.delete(`/seasons/${editingSeason.id}/rookie-soph/${levelId}`);
      fetchRookieSoph(editingSeason.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Error deleting level');
    }
  };

  const handleRSCancel = () => {
    setShowRSForm(false);
    setEditingRSLevel(null);
  };

  // ── Attribute Upgrades handler ────────────────────────────────────────────

  const handleAttributeUpgradeSave = async () => {
    setAttributeUpgradeError('');
    const { attributeUpgradeGames, attributeUpgradePoints } = attributeUpgradeSettings;

    if (attributeUpgradeGames !== '' && attributeUpgradeGames !== null) {
      const val = parseInt(attributeUpgradeGames, 10);
      if (isNaN(val) || val < 1) {
        setAttributeUpgradeError(t('admin.seasonManagement.validation.attrGamesInvalid'));
        return;
      }
    }
    if (attributeUpgradePoints !== '' && attributeUpgradePoints !== null) {
      const val = parseInt(attributeUpgradePoints, 10);
      if (isNaN(val) || val < 1) {
        setAttributeUpgradeError(t('admin.seasonManagement.validation.attrPointsInvalid'));
        return;
      }
    }

    setAttributeUpgradeSaving(true);
    try {
      await api.put(`/seasons/${editingSeason.id}/attribute-upgrades`, {
        attributeUpgradeGames: attributeUpgradeGames === '' ? null : parseInt(attributeUpgradeGames, 10),
        attributeUpgradePoints: attributeUpgradePoints === '' ? null : parseInt(attributeUpgradePoints, 10)
      });
      alert(t('admin.seasonManagement.attributeUpgradesSaved'));
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      setAttributeUpgradeError(msg);
    } finally {
      setAttributeUpgradeSaving(false);
    }
  };

  // ── Roster import helpers ──────────────────────────────────────────────────

  const csvTemplate = `First Name,Last Name,Position,Overall Rating,Team Name,Badges
Shaquille,ONeal,C,96,Lakers,18
Kobe,Bryant,SG,94,Lakers,15
Magic,Johnson,PG,95,Lakers,16`;

  const handleImportRoster = async (seasonId, file) => {
    try {
      const fd = new FormData();
      fd.append('csvFile', file);
      fd.append('seasonId', seasonId);
      await api.post(`/seasons/${seasonId}/import-roster`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Roster imported successfully!');
      fetchSeasons();
    } catch (error) {
      console.error('Error importing roster:', error);
      alert('Failed to import roster: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFileUpload = (event, seasonId) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      if (window.confirm('Import this CSV file as the custom roster for this season?')) {
        handleImportRoster(seasonId, file);
      }
    } else {
      alert('Please select a valid CSV file');
    }
    event.target.value = '';
  };

  // ── Badge helpers ──────────────────────────────────────────────────────────

  const getStatusBadge = (season) => {
    if (season.completed)
      return <span className="px-2 py-1 bg-gray-50 dark:bg-gray-8000 text-white text-xs rounded">Completed</span>;
    if (season.playoffsStarted)
      return <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded">Playoffs</span>;
    if (season.isActive)
      return <span className="px-2 py-1 bg-green-500 text-white text-xs rounded">Active</span>;
    return <span className="px-2 py-1 bg-gray-300 text-gray-700 dark:text-gray-300 text-xs rounded">Inactive</span>;
  };

  const getSeasonTypeBadge = (season) => {
    if (season.seasonType === 'custom') {
      const rosterStatus = season.customRosterImported ? '✅' : '⏳';
      const fantasyDraftStatus = season.hasFantasyDraft
        ? (season.fantasyDraftCompleted ? '🏆' : season.fantasyDraftStarted ? '🎯' : '📋')
        : '';
      return (
        <div className="flex flex-col space-y-1">
          <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded">
            Custom {rosterStatus}{fantasyDraftStatus}
          </span>
          {season.rosterEra && <span className="text-xs text-gray-500 dark:text-gray-400">{season.rosterEra}</span>}
          {season.hasFantasyDraft && (
            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
              {season.fantasyDraftCompleted ? 'Draft Complete' :
                season.fantasyDraftStarted ? 'Draft In Progress' : 'Draft Pending'}
            </span>
          )}
        </div>
      );
    }
    return <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/200 text-white text-xs rounded">Standard</span>;
  };

  // ── Tab helper ─────────────────────────────────────────────────────────────

  const tabClass = (name) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      activeTab === name
        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
    }`;

  if (loading) {
    return <div className="text-center py-12">Loading seasons...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Season Management</h2>
        <button
          onClick={() => showCreateForm ? resetForm() : setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          {showCreateForm ? 'Cancel' : '+ Create New Season'}
        </button>
      </div>

      {/* Create/Edit Panel */}
      {showCreateForm && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
          <h3 className="text-xl font-semibold mb-4">
            {editingSeason ? 'Edit Season' : 'Create New Season'}
          </h3>

          {/* Tabs — only shown when editing an existing season */}
          {editingSeason && (
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
              <nav className="-mb-px flex space-x-6">
                <button className={tabClass('setup')} onClick={() => setActiveTab('setup')}>
                  {t('admin.seasonManagement.setupTab')}
                </button>
                <button className={tabClass('general')} onClick={() => setActiveTab('general')}>
                  {t('admin.seasonManagement.generalSettingsTab')}
                </button>
                <button className={tabClass('gameplay')} onClick={() => setActiveTab('gameplay')}>
                  {t('admin.seasonManagement.gameplaySettingsTab')}
                </button>
                <button className={tabClass('seasonpass')} onClick={() => setActiveTab('seasonpass')}>
                  {t('admin.seasonManagement.seasonPassTab')}
                </button>
                <button className={tabClass('rookiesoph')} onClick={() => setActiveTab('rookiesoph')}>
                  {t('admin.seasonManagement.rookieSophTab')}
                </button>
                <button className={tabClass('attributeupgrades')} onClick={() => setActiveTab('attributeupgrades')}>
                  {t('admin.seasonManagement.attributeUpgradesTab')}
                </button>
              </nav>
            </div>
          )}

          {/* ── Setup Assistant Tab (AP6) ── */}
          {editingSeason && activeTab === 'setup' && (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    {t('admin.seasonManagement.setupTitle')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('admin.seasonManagement.setupDescription')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => fetchSetupStatus(editingSeason.id)}
                  disabled={setupLoading}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {t('admin.seasonManagement.setupRefresh')}
                </button>
              </div>

              {setupError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-2 rounded">
                  {setupError}
                </div>
              )}
              {setupMessage && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-2 rounded">
                  {setupMessage}
                </div>
              )}

              {setupLoading && !setupStatus ? (
                <p className="text-gray-500 dark:text-gray-400">{t('admin.seasonManagement.setupLoading')}</p>
              ) : setupStatus ? (
                <ul className="space-y-3">
                  {[
                    {
                      key: 'roster',
                      done: setupStatus.roster.count > 0,
                      label: t('admin.seasonManagement.setupStepRoster'),
                      status: t('admin.seasonManagement.setupStepRosterStatus', { count: setupStatus.roster.count }),
                      action: (
                        <button
                          type="button"
                          onClick={handleMaterializeRoster}
                          disabled={materializing}
                          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                        >
                          {materializing ? t('admin.seasonManagement.setupMaterializing') : t('admin.seasonManagement.setupActionMaterialize')}
                        </button>
                      )
                    },
                    {
                      key: 'games',
                      done: setupStatus.games.total > 0,
                      label: t('admin.seasonManagement.setupStepGames'),
                      status: t('admin.seasonManagement.setupStepGamesStatus', { total: setupStatus.games.total, played: setupStatus.games.played }),
                      action: (
                        <button type="button" onClick={() => navigate('/admin/games')} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoGames')}
                        </button>
                      )
                    },
                    {
                      key: 'deadlines',
                      done: setupStatus.deadlines.count > 0,
                      label: t('admin.seasonManagement.setupStepDeadlines'),
                      status: t('admin.seasonManagement.setupStepCountStatus', { count: setupStatus.deadlines.count }),
                      action: (
                        <button type="button" onClick={() => setActiveTab('gameplay')} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoTab')}
                        </button>
                      )
                    },
                    {
                      key: 'seasonPass',
                      done: setupStatus.seasonPass.count > 0,
                      label: t('admin.seasonManagement.setupStepSeasonPass'),
                      status: t('admin.seasonManagement.setupStepCountStatus', { count: setupStatus.seasonPass.count }),
                      action: (
                        <button type="button" onClick={() => setActiveTab('seasonpass')} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoTab')}
                        </button>
                      )
                    },
                    {
                      key: 'rookieSoph',
                      done: setupStatus.rookieSoph.count > 0,
                      label: t('admin.seasonManagement.setupStepRookieSoph'),
                      status: t('admin.seasonManagement.setupStepCountStatus', { count: setupStatus.rookieSoph.count }),
                      action: (
                        <button type="button" onClick={() => setActiveTab('rookiesoph')} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoTab')}
                        </button>
                      )
                    },
                    {
                      key: 'freeAgency',
                      done: setupStatus.freeAgency.exists,
                      label: t('admin.seasonManagement.setupStepFreeAgency'),
                      status: setupStatus.freeAgency.exists
                        ? t('admin.seasonManagement.setupStepFreeAgencyYes')
                        : t('admin.seasonManagement.setupStepFreeAgencyNo'),
                      action: (
                        <button type="button" onClick={() => navigate(`/admin/free-agency/${editingSeason.id}`)} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoFreeAgency')}
                        </button>
                      )
                    },
                    {
                      key: 'gms',
                      done: setupStatus.gms.withTeam > 0,
                      label: t('admin.seasonManagement.setupStepGms'),
                      status: t('admin.seasonManagement.setupStepGmsStatus', { count: setupStatus.gms.withTeam }),
                      action: (
                        <button type="button" onClick={() => navigate('/admin/users')} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          {t('admin.seasonManagement.setupActionGoUsers')}
                        </button>
                      )
                    },
                    {
                      key: 'gmHistory',
                      done: (setupStatus.gmHistory?.count || 0) > 0,
                      label: t('admin.seasonManagement.setupStepGmHistory'),
                      status: t('admin.seasonManagement.setupStepGmHistoryStatus', { count: setupStatus.gmHistory?.count || 0 }),
                      action: (
                        <button
                          type="button"
                          onClick={handleSnapshotGMs}
                          disabled={snapshotting}
                          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                          {snapshotting ? t('admin.seasonManagement.setupSnapshotting') : t('admin.seasonManagement.setupActionSnapshot')}
                        </button>
                      )
                    }
                  ].map((step) => (
                    <li key={step.key} className="flex items-center gap-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm ${step.done ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
                        {step.done ? '✓' : '○'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-100">{step.label}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{step.status}</div>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${step.done ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'}`}>
                        {step.done ? t('admin.seasonManagement.setupDone') : t('admin.seasonManagement.setupOpen')}
                      </span>
                      <div className="flex-shrink-0">{step.action}</div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}

          {/* ── General Settings Tab (or create form) ── */}
          {(!editingSeason || activeTab === 'general') && (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Season Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="e.g., 2024-25 Season"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Year *
                  </label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Playoff Format
                  </label>
                  <select
                    value={formData.playoffFormat}
                    onChange={(e) => {
                      const newFormat = e.target.value;
                      setFormData({ ...formData, playoffFormat: newFormat });
                      if (newFormat === 'custom') {
                        setCustomPlayoffConfig(prev => ({
                          ...prev,
                          rounds: prev.rounds.length > 0 ? prev.rounds : getDefaultRounds(prev.participants)
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  >
                    <option value="conference">Conference Format (8 teams per conference)</option>
                    <option value="top8">Top 8 Overall (regardless of conference)</option>
                    <option value="top16">Top 16 Overall (regardless of conference)</option>
                    <option value="top10_playin">Top 10 Play-In (6 direct + 4 play-in, overall)</option>
                    <option value="custom">Custom Playoffs (fully configurable)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.playoffFormat === 'conference' &&
                      'Traditional NBA format: Top 8 teams from each conference with play-in tournament.'}
                    {formData.playoffFormat === 'top8' &&
                      'Best 8 teams overall compete regardless of conference. Standard 1v8, 2v7, 3v6, 4v5 bracket.'}
                    {formData.playoffFormat === 'top16' &&
                      'Best 16 teams overall compete regardless of conference. Standard 1v16, 2v15, etc. bracket.'}
                    {formData.playoffFormat === 'top10_playin' &&
                      'Top 10 teams overall: seeds 1-6 advance directly, seeds 7-10 compete in play-in tournament (7v8, 9v10, then elimination game).'}
                    {formData.playoffFormat === 'custom' &&
                      'Fully configurable: set number of participants, conference dependency, and per-round series format.'}
                  </p>
                </div>

                {/* Custom Playoff Configuration */}
                {formData.playoffFormat === 'custom' && (
                  <div className="col-span-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">Custom Playoff Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Number of Participants
                        </label>
                        <select
                          value={customPlayoffConfig.participants}
                          onChange={(e) => {
                            const participants = parseInt(e.target.value);
                            const newRounds = getDefaultRounds(participants);
                            setCustomPlayoffConfig({ ...customPlayoffConfig, participants, rounds: newRounds });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        >
                          <option value={4}>4 Teams</option>
                          <option value={8}>8 Teams</option>
                          <option value={16}>16 Teams</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Conference Dependent
                        </label>
                        <select
                          value={customPlayoffConfig.conferenceDependent ? 'yes' : 'no'}
                          onChange={(e) => setCustomPlayoffConfig({ ...customPlayoffConfig, conferenceDependent: e.target.value === 'yes' })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        >
                          <option value="no">No — Overall standings</option>
                          <option value="yes">Yes — Separate by conference</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Rounds Configuration ({customPlayoffConfig.rounds.length} rounds)
                      </label>
                      {customPlayoffConfig.rounds.map((round, idx) => {
                        const teamsInRound = customPlayoffConfig.participants / Math.pow(2, idx);
                        return (
                          <div key={idx} className="flex items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
                              {teamsInRound} → {teamsInRound / 2} teams
                            </span>
                            <input
                              type="text"
                              value={round.name}
                              onChange={(e) => {
                                const newRounds = [...customPlayoffConfig.rounds];
                                newRounds[idx] = { ...newRounds[idx], name: e.target.value };
                                setCustomPlayoffConfig({ ...customPlayoffConfig, rounds: newRounds });
                              }}
                              placeholder="Round name"
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                            />
                            <select
                              value={round.seriesFormat}
                              onChange={(e) => {
                                const newRounds = [...customPlayoffConfig.rounds];
                                newRounds[idx] = { ...newRounds[idx], seriesFormat: e.target.value };
                                setCustomPlayoffConfig({ ...customPlayoffConfig, rounds: newRounds });
                              }}
                              className="w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                            >
                              <option value="Best-of-1">Single Game</option>
                              <option value="Best-of-3">Best-of-3</option>
                              <option value="Best-of-5">Best-of-5</option>
                              <option value="Best-of-7">Best-of-7</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>

                    {/* Preview */}
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Preview:</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {customPlayoffConfig.participants} teams, {customPlayoffConfig.conferenceDependent ? 'conference-dependent' : 'overall standings'} →{' '}
                        {customPlayoffConfig.rounds.map((r, i) => (
                          <span key={i}>
                            {i > 0 && ' → '}
                            <strong>{r.name}</strong> ({r.seriesFormat})
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                )}

                {formData.playoffFormat !== 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Series Format
                  </label>
                  <select
                    value={formData.seriesFormat}
                    onChange={(e) => setFormData({ ...formData, seriesFormat: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  >
                    <option value="Best-of-3">Best-of-3 (First to 2 wins)</option>
                    <option value="Best-of-5">Best-of-5 (First to 3 wins)</option>
                    <option value="Best-of-7">Best-of-7 (First to 4 wins)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Default series length for playoff rounds (Play-In is always single game)
                  </p>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Set as Active Season
                  </label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 mt-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Can't Cut Rule Active
                  </label>
                  <input
                    type="checkbox"
                    checked={formData.cantCutRuleActive}
                    onChange={(e) => setFormData({ ...formData, cantCutRuleActive: e.target.checked })}
                    className="w-5 h-5 mt-2"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    When enabled, teams must assign Can't Cut players and cannot trade them. Disable to allow all trades without restriction.
                  </p>
                </div>
              </div>

              {/* Season Type Selection */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Season Type *
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="seasonType"
                      value="standard"
                      checked={formData.seasonType === 'standard'}
                      onChange={(e) => setFormData({ ...formData, seasonType: e.target.value, rosterEra: '' })}
                      className="mr-3"
                    />
                    <div>
                      <span className="font-medium">Standard Season</span>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Use current NBA rosters already in the database</p>
                    </div>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="seasonType"
                      value="custom"
                      checked={formData.seasonType === 'custom'}
                      onChange={(e) => setFormData({ ...formData, seasonType: e.target.value })}
                      className="mr-3"
                    />
                    <div>
                      <span className="font-medium">Custom Season</span>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Import your own custom roster (e.g., 2000 Era, All-Stars, etc.)</p>
                    </div>
                  </label>
                </div>

                {formData.seasonType === 'custom' && (
                  <div className="mt-4 space-y-4 pl-6 border-l-2 border-blue-300">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Roster Era/Description
                      </label>
                      <input
                        type="text"
                        value={formData.rosterEra}
                        onChange={(e) => setFormData({ ...formData, rosterEra: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        placeholder="e.g., 2000 Era, 90s Legends, All-Time Greats"
                        required={formData.seasonType === 'custom'}
                      />
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                      <h4 className="font-medium text-blue-800 mb-2">📥 Import Custom Roster</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                        After creating this season, you'll need to import your custom roster via CSV file.
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📋 CSV Template (copy and paste into your file):</span>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(csvTemplate)}
                            className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 transition"
                          >
                            Copy
                          </button>
                        </div>
                        <pre className="text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded p-3 overflow-x-auto font-mono">
                          {csvTemplate}
                        </pre>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Save this as a .csv file and import it after creating the season.
                        </p>
                      </div>
                    </div>

                    {/* Fantasy Draft Option */}
                    <div className="bg-green-50 p-3 rounded">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="hasFantasyDraft"
                          checked={formData.hasFantasyDraft}
                          onChange={(e) => setFormData({ ...formData, hasFantasyDraft: e.target.checked })}
                          className="mr-3"
                        />
                        <label htmlFor="hasFantasyDraft" className="font-medium text-green-800">
                          🎯 Use Fantasy Draft
                        </label>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                        Create a fantasy draft where GMs draft players to build their teams instead of importing rosters.
                      </p>

                      {formData.hasFantasyDraft && (
                        <div className="space-y-3 pl-4 border-l-2 border-green-300">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Draft Name
                            </label>
                            <input
                              type="text"
                              value={formData.fantasyDraftName}
                              onChange={(e) => setFormData({ ...formData, fantasyDraftName: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                              placeholder="e.g., 2026 Fantasy Draft"
                              required={formData.hasFantasyDraft}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Draft Rounds
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={formData.draftRounds}
                                onChange={(e) => setFormData({ ...formData, draftRounds: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Draft Order
                              </label>
                              <select
                                value={formData.draftOrder}
                                onChange={(e) => setFormData({ ...formData, draftOrder: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                              >
                                <option value="standard">Standard (1,2,3...16 each round)</option>
                                <option value="snake">Snake (1,2,3...16, then 16,15,14...1)</option>
                              </select>
                            </div>
                          </div>

                          <div className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 p-2 rounded">
                            💡 <strong>Note:</strong> You can assign GMs to teams and start the fantasy draft later through the Edit Season option.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
                >
                  {editingSeason ? 'Update Season' : 'Create Season'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-300 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* ── Gameplay Settings Tab ── */}
          {editingSeason && activeTab === 'gameplay' && (
            <div className="space-y-8">

              {/* Section 1: Season Settings */}
              <section>
                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  {t('admin.seasonManagement.seasonSettings')}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t('admin.seasonManagement.seasonSettingsDescription')}
                </p>

                {gameplaySettingsError && (
                  <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {gameplaySettingsError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.seasonManagement.maxGames')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={gameplaySettings.maxGames}
                      onChange={(e) => setGameplaySettings({ ...gameplaySettings, maxGames: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder={t('admin.seasonManagement.noLimit')}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.seasonManagement.maxGamesHelp')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.seasonManagement.minGamesForPlayoffs')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={gameplaySettings.minGamesForPlayoffs}
                      onChange={(e) => setGameplaySettings({ ...gameplaySettings, minGamesForPlayoffs: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.seasonManagement.minGamesForPlayoffsHelp')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.seasonManagement.maxTradesPerTeam')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={gameplaySettings.maxTradesPerTeam}
                      onChange={(e) => setGameplaySettings({ ...gameplaySettings, maxTradesPerTeam: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder={t('admin.seasonManagement.noLimit')}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.seasonManagement.maxTradesPerTeamHelp')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.seasonManagement.maxTradedPlayersPerTeam')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={gameplaySettings.maxTradedPlayersPerTeam}
                      onChange={(e) => setGameplaySettings({ ...gameplaySettings, maxTradedPlayersPerTeam: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder={t('admin.seasonManagement.noLimit')}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.seasonManagement.maxTradedPlayersPerTeamHelp')}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGameplaySettingsSave}
                  disabled={gameplaySettingsSaving}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-60"
                >
                  {gameplaySettingsSaving ? t('common.saving') : t('admin.seasonManagement.saveGameplaySettings')}
                </button>
              </section>

              {/* Divider */}
              <hr className="border-gray-200 dark:border-gray-700" />

              {/* Section 2: Deadlines */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                      {t('admin.seasonManagement.deadlines')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('admin.seasonManagement.deadlinesDescription')}
                    </p>
                  </div>
                  {!showDeadlineForm && (
                    <button
                      type="button"
                      onClick={() => { setEditingDeadline(null); setShowDeadlineForm(true); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                      + {t('admin.seasonManagement.addDeadline')}
                    </button>
                  )}
                </div>

                {/* Inline deadline form (add / edit) */}
                {showDeadlineForm && (
                  <div className="mb-5">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {editingDeadline
                        ? t('admin.seasonManagement.editDeadline')
                        : t('admin.seasonManagement.addDeadline')}
                    </h5>
                    <DeadlineForm
                      initial={editingDeadline
                        ? {
                            label: editingDeadline.label,
                            startDate: editingDeadline.startDate,
                            endDate: editingDeadline.endDate,
                            minGames: editingDeadline.minGames,
                            maxGames: editingDeadline.maxGames !== null && editingDeadline.maxGames !== undefined
                              ? editingDeadline.maxGames
                              : ''
                          }
                        : null}
                      onSave={handleDeadlineSave}
                      onCancel={handleDeadlineCancel}
                      t={t}
                    />
                  </div>
                )}

                {/* Deadlines table */}
                {deadlinesLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Loading deadlines...</div>
                ) : deadlines.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg py-6 text-center">
                    {t('admin.seasonManagement.noDeadlines')}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.deadlineLabel')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.deadlineStartDate')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.deadlineEndDate')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.deadlineMinGames')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.deadlineMaxGames')}</th>
                          <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {deadlines.map((d) => (
                          <tr key={d.id} className="hover:bg-gray-50 dark:bg-gray-800">
                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{d.label}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                              {new Date(d.startDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                              {new Date(d.endDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium">{d.minGames}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                              {d.maxGames !== null && d.maxGames !== undefined
                                ? d.maxGames
                                : <span className="text-gray-400 italic">{t('admin.seasonManagement.noLimit')}</span>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleDeadlineEdit(d)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm"
                                >
                                  {t('common.edit')}
                                </button>
                                <button
                                  onClick={() => handleDeadlineDelete(d.id)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm"
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ── Season Pass Tab ── */}
          {editingSeason && activeTab === 'seasonpass' && (
            <div className="space-y-6">
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                      {t('admin.seasonManagement.seasonPass')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('admin.seasonManagement.seasonPassDescription')}
                    </p>
                  </div>
                  {!showLevelForm && (
                    <button
                      type="button"
                      onClick={() => { setEditingLevel(null); setShowLevelForm(true); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                      + {t('admin.seasonManagement.addLevel')}
                    </button>
                  )}
                </div>

                {/* Inline level form (add / edit) */}
                {showLevelForm && (
                  <div className="mb-5">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {editingLevel
                        ? t('admin.seasonManagement.editLevel')
                        : t('admin.seasonManagement.addLevel')}
                    </h5>
                    <LevelForm
                      key={editingLevel ? editingLevel.id : 'new'}
                      initial={editingLevel
                        ? {
                            gamesRequired: editingLevel.gamesRequired,
                            rewardDescription: editingLevel.rewardDescription,
                            icon: editingLevel.icon,
                            rewardT1: editingLevel.rewardT1 || '',
                            rewardT2: editingLevel.rewardT2 || '',
                            rewardT3: editingLevel.rewardT3 || '',
                            rewardT4: editingLevel.rewardT4 || ''
                          }
                        : null}
                      nextLevel={editingLevel ? editingLevel.level : seasonPassLevels.length + 1}
                      maxGames={editingSeason.maxGames}
                      onSave={handleLevelSave}
                      onCancel={handleLevelCancel}
                      t={t}
                    />
                  </div>
                )}

                {/* Levels table */}
                {seasonPassLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Loading season pass...</div>
                ) : seasonPassLevels.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg py-6 text-center">
                    {t('admin.seasonManagement.noLevels')}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.levelNumber')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.icon')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.gamesRequired')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.rewardDescription')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.tierColumn')}</th>
                          <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {seasonPassLevels.map((lvl) => (
                          <tr key={lvl.id} className="hover:bg-gray-50 dark:bg-gray-800">
                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-gray-100">{lvl.level}</td>
                            <td className="px-4 py-3 text-xl">{getIconEmoji(lvl.icon) || <span className="text-gray-300 text-sm">—</span>}</td>
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium">{lvl.gamesRequired}</td>
                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{lvl.rewardDescription}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                {['T1', 'T2', 'T3', 'T4'].map((tier) => {
                                  const text = lvl[`reward${tier}`];
                                  const filled = !!(text && text.trim());
                                  return (
                                    <span
                                      key={tier}
                                      title={filled ? text : t('admin.seasonManagement.tierEmpty', { tier })}
                                      className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                        filled
                                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                                      }`}
                                    >
                                      {tier}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleLevelEdit(lvl)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm"
                                >
                                  {t('common.edit')}
                                </button>
                                <button
                                  onClick={() => handleLevelDelete(lvl.id)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm"
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ── Rookies & Sophs Tab ── */}
          {editingSeason && activeTab === 'rookiesoph' && (
            <div className="space-y-6">
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                      {t('admin.seasonManagement.rookieSoph')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('admin.seasonManagement.rookieSophDescription')}
                    </p>
                  </div>
                  {!showRSForm && (
                    <button
                      type="button"
                      onClick={() => { setEditingRSLevel(null); setShowRSForm(true); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                      + {t('admin.seasonManagement.addLevel')}
                    </button>
                  )}
                </div>

                {showRSForm && (
                  <div className="mb-5">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {editingRSLevel
                        ? t('admin.seasonManagement.editLevel')
                        : t('admin.seasonManagement.addLevel')}
                    </h5>
                    <RookieSophForm
                      initial={editingRSLevel
                        ? {
                            gamesRequired: editingRSLevel.gamesRequired,
                            upgradeDescription: editingRSLevel.upgradeDescription,
                            limitationDescription: editingRSLevel.limitationDescription || '',
                            icon: editingRSLevel.icon
                          }
                        : null}
                      nextLevel={editingRSLevel ? editingRSLevel.level : rookieSophLevels.length + 1}
                      maxGames={editingSeason.maxGames}
                      onSave={handleRSSave}
                      onCancel={handleRSCancel}
                      t={t}
                    />
                  </div>
                )}

                {rookieSophLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Loading...</div>
                ) : rookieSophLevels.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg py-6 text-center">
                    {t('admin.seasonManagement.noRookieSophLevels')}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.levelNumber')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.icon')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.gamesRequired')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.upgradeDescription')}</th>
                          <th className="px-4 py-3 text-left">{t('admin.seasonManagement.limitationDescription')}</th>
                          <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rookieSophLevels.map((lvl) => (
                          <tr key={lvl.id} className="hover:bg-gray-50 dark:bg-gray-800">
                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-gray-100">{lvl.level}</td>
                            <td className="px-4 py-3 text-xl">{getIconEmoji(lvl.icon) || <span className="text-gray-300 text-sm">—</span>}</td>
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium">{lvl.gamesRequired}</td>
                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{lvl.upgradeDescription}</td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{lvl.limitationDescription || '—'}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleRSEdit(lvl)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm"
                                >
                                  {t('common.edit')}
                                </button>
                                <button
                                  onClick={() => handleRSDelete(lvl.id)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm"
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ── Attribute Upgrades Tab ── */}
          {editingSeason && activeTab === 'attributeupgrades' && (
            <div className="space-y-6">
              <section>
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                    {t('admin.seasonManagement.attributeUpgrades')}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('admin.seasonManagement.attributeUpgradesDescription')}
                  </p>
                </div>

                {attributeUpgradeError && (
                  <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">
                    {attributeUpgradeError}
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('admin.seasonManagement.attrGamesInterval')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={attributeUpgradeSettings.attributeUpgradeGames}
                        onChange={e => setAttributeUpgradeSettings(prev => ({ ...prev, attributeUpgradeGames: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        placeholder={t('admin.seasonManagement.attrGamesPlaceholder')}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('admin.seasonManagement.attrGamesHelp')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('admin.seasonManagement.attrPoints')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={attributeUpgradeSettings.attributeUpgradePoints}
                        onChange={e => setAttributeUpgradeSettings(prev => ({ ...prev, attributeUpgradePoints: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        placeholder={t('admin.seasonManagement.attrPointsPlaceholder')}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('admin.seasonManagement.attrPointsHelp')}
                      </p>
                    </div>
                  </div>

                  {attributeUpgradeSettings.attributeUpgradeGames && attributeUpgradeSettings.attributeUpgradePoints && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-400">
                      <strong>{t('admin.seasonManagement.attrPreview')}:</strong>{' '}
                      {t('admin.seasonManagement.attrPreviewText', {
                        games: attributeUpgradeSettings.attributeUpgradeGames,
                        points: attributeUpgradeSettings.attributeUpgradePoints
                      })}
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleAttributeUpgradeSave}
                      disabled={attributeUpgradeSaving}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50"
                    >
                      {attributeUpgradeSaving ? t('common.saving') : t('common.save')}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      )}

      {/* Seasons List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold">All Seasons ({seasons.length})</h3>
        </div>

        {seasons.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            No seasons found. Create your first season to get started!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Games</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {seasons.map((season) => (
                  <tr key={season.id} className="hover:bg-gray-50 dark:bg-gray-800">
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">{season.name}</td>
                    <td className="px-6 py-4">{getSeasonTypeBadge(season)}</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{season.year}</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                      {new Date(season.startDate).toLocaleDateString()}
                      {season.endDate && ` - ${new Date(season.endDate).toLocaleDateString()}`}
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <span>{season.playedGames || 0} / {season.totalGames || 0}</span>
                        {season.totalGames > 0 && (
                          <span className="text-amber-500" title="Season has games - deletion requires confirmation">
                            ⚠️
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(season)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {!season.isActive && (
                          <button
                            onClick={() => handleActivate(season.id)}
                            className="text-green-600 dark:text-green-400 hover:text-green-800 text-sm"
                          >
                            Activate
                          </button>
                        )}
                        {season.seasonType === 'custom' && !season.customRosterImported && (
                          <div className="relative">
                            <input
                              type="file"
                              accept=".csv"
                              onChange={(e) => handleFileUpload(e, season.id)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              id={`import-${season.id}`}
                            />
                            <label
                              htmlFor={`import-${season.id}`}
                              className="text-purple-600 hover:text-purple-800 text-sm cursor-pointer font-medium"
                              title="Import custom roster CSV file"
                            >
                              📥 Import Roster
                            </label>
                          </div>
                        )}
                        {season.seasonType === 'custom' && season.hasFantasyDraft && (
                          <button
                            onClick={() => window.open(`/admin/fantasy-draft/${season.id}`, '_blank')}
                            className="text-green-600 dark:text-green-400 hover:text-green-800 text-sm font-medium"
                            title="Manage fantasy draft"
                          >
                            🎯 {season.fantasyDraftCompleted ? 'View Draft' : 'Manage Draft'}
                          </button>
                        )}
                        <button
                          onClick={() => window.open(`/admin/free-agency/${season.id}`, '_blank')}
                          className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                          title="Manage free agency"
                        >
                          🏀 Free Agency
                        </button>
                        <button
                          onClick={() => handleEdit(season)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(season.id)}
                          className={`text-sm font-medium ${
                            season.totalGames > 0
                              ? 'text-amber-600 hover:text-amber-800'
                              : 'text-red-600 dark:text-red-400 hover:text-red-800'
                          }`}
                          title={season.totalGames > 0 ? `⚠️ Will require confirmation to delete ${season.totalGames} game(s)` : 'Delete season'}
                        >
                          {season.totalGames > 0 ? '⚠️ Delete' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonManagement;
